import React, { useState } from 'react';
import { Alert, Button, Form, InputNumber, Select, Tag } from 'antd';
import {
  PgProviderAccountType,
  PgProviderRouteItem,
  PgProviderRoutesMaster,
} from '../../../../lib/api/pgProviderRoute';
import { PG_PROVIDER_ACCOUNT_TYPE_LABEL } from '../../constants/labels';
import { resolveRoute } from '../../utils/resolveRoute';
import { SettingsSection } from '../../common/SettingsSection';
import { RouteSimulatorStyle } from './styles';

type RouteSimulatorProps = { routes: PgProviderRouteItem[]; master: PgProviderRoutesMaster; isDirty: boolean };

export const RouteSimulator = (props: RouteSimulatorProps): React.JSX.Element => {
  const { routes, master, isDirty } = props;
  const [selectedMethod, setSelectedMethod] = useState<number>();
  const [accountType, setAccountType] = useState<PgProviderAccountType>('general');
  const [amount, setAmount] = useState<number | null>(50000);
  const payMethodId =
    selectedMethod ?? master.payMethods.find((method) => method.type === 'card')?.id ?? master.payMethods[0]?.id;
  const result =
    payMethodId && amount !== null && Number.isInteger(amount) && amount > 0
      ? resolveRoute(routes, master, { payMethodId, accountType, amount })
      : null;
  const presetList = [
    ...new Set(
      routes
        .filter(
          (route) =>
            route.isActive &&
            route.payMethodId === payMethodId &&
            (route.accountType === null || route.accountType === accountType),
        )
        .flatMap((route) =>
          route.maxAmount === null ? [] : [route.maxAmount - 1, route.maxAmount, route.maxAmount + 1],
        ),
    ),
  ]
    .filter((value) => value > 0)
    .sort((a, b) => a - b);
  const matchedIndex = routes.findIndex((route) => route.priority === result?.matchedPriority);
  // user-api `paymentIamport.ctrl.js` 의 v6 경로가 `nice_v2` 를 `nice` 로 바꿔 요청한다.
  // 시뮬레이터는 DB 값 그대로 보여주므로, 그 차이를 여기서 알려 준다.
  const LEGACY_NICE_MODULE_CODE = 'nice_v2';
  return (
    <SettingsSection
      title="결과 확인"
      action={<Tag color={isDirty ? 'gold' : 'default'}>{isDirty ? '편집 중인 설정' : '저장된 설정'}</Tag>}
    >
      <RouteSimulatorStyle className="settings-section-body">
        <Form layout="vertical" component="div">
          <Form.Item label="결제수단">
            <Select
              aria-label="결과 확인 결제수단"
              value={payMethodId}
              options={master.payMethods.map((method) => ({ value: method.id, label: method.name }))}
              onChange={setSelectedMethod}
            />
          </Form.Item>
          <Form.Item label="계정 유형">
            <Select
              aria-label="결과 확인 계정 유형"
              value={accountType}
              options={Object.entries(PG_PROVIDER_ACCOUNT_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={setAccountType}
            />
          </Form.Item>
          <Form.Item label="결제 금액">
            <InputNumber
              controls={false}
              aria-label="결과 확인 결제 금액"
              value={amount}
              min={1}
              precision={0}
              addonAfter="원"
              formatter={(value) => String(value ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => Number((value ?? '').replace(/,/g, ''))}
              onChange={setAmount}
            />
          </Form.Item>
        </Form>
        {presetList.length > 0 && (
          <>
            <span className="settings-muted">경계 금액 빠른 확인</span>
            <div className="simulation-presets">
              {presetList.map((value) => (
                <Button
                  key={value}
                  size="small"
                  type={value === amount ? 'primary' : 'default'}
                  onClick={() => setAmount(value)}
                >
                  {value.toLocaleString('ko-KR')}원
                </Button>
              ))}
            </div>
          </>
        )}
        <div className="simulation-result" aria-live="polite">
          {result ? (
            <>
              <span className="settings-muted">
                {PG_PROVIDER_ACCOUNT_TYPE_LABEL[accountType]} · {amount?.toLocaleString('ko-KR')}원
              </span>
              <strong className="simulation-pg">{result.pgProviderKorName ?? '연결 PG 없음'}</strong>
              {result.pgCode ? (
                <span className="settings-muted">
                  {result.isFallback ? '일치하는 규칙 없음 · 기본 연결 사용' : '규칙 ' + (matchedIndex + 1) + ' 적용'}
                </span>
              ) : (
                <Alert type="error" message="연결 가능한 MID가 없습니다." />
              )}
              <details className="simulation-account">
                <summary>연결 계정(MID)</summary>
                {result.pgCode ?? '미등록'}
                {result.pgCode?.startsWith(LEGACY_NICE_MODULE_CODE + '.') && (
                  <p className="settings-muted">
                    PC 웹 결제창(v6)만 같은 MID 를 <code>nice</code> 모듈로 요청합니다. MID 는 동일합니다.
                  </p>
                )}
              </details>
            </>
          ) : (
            <span className="settings-muted">1원 이상의 금액을 입력하세요.</span>
          )}
        </div>
        <p className="simulation-note">
          라우팅 규칙만 확인합니다. 실제 결제나 PG 승인 가능 여부, 상품별 결제수단 노출 여부를 검증하지 않습니다.
        </p>
      </RouteSimulatorStyle>
    </SettingsSection>
  );
};
